import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { ModalGameCenterTabs, GameCenterFormTypes } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  createTag,
  getTagItem,
  updateTag,
  handleClearFields,
  handleUpdateFields as handleUpdateTagFields,
  getTagItemLocale,
  updateTagItemLocale,
} from "store/gameCenter/tags/slice";
import {
  createCollection,
  getCollectionItem,
  updateCollection,
  handleClearFields as handleClearFieldsCollections,
  handleUpdateFields as handleUpdateCollectionFields,
  getCollectionItemLocale,
  updateCollectionItemLocale,
} from "store/gameCenter/collections/slice";
import {
  createCategory,
  handleClearFields as handleClearFieldsCategorries,
  getCategoryItem,
  updateCategory,
  handleUpdateFields,
  updateCategoryItemLocale,
  getCategoryItemLocale,
} from "store/gameCenter/categories/slice";

import { NotificationTypes, showNotification } from "store/notifications/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import CommonMainTab from "./tabs/CommonMainTab";
import TagsOrderListTab from "./tabs/tags/TagsOrderListTab";
import SeoTab from "./tabs/SeoTab";

import RestrictionTab from "./tabs/RestrictionTab";
import { Modal } from "components/Modal/Modal";
import styles from "./GameCenterCommonModal.module.scss";
import TrailTab from "./tabs/TrailTab";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import LocalTab, { FieldUpdate as TlocaleFieldUpdate } from "./tabs/LocalTab";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { isNumericOnlySlug } from "helpers/numericEntitySlug";
import {
  getSeoFieldErrors,
  getChangedSeoAttributes,
  normalizeSeoHtml,
} from "helpers/seoAttributes";

type Props = {
  entityIdOverride?: string;
  modalTypeOverride?: GameCenterFormTypes;
  onCloseOverride?: () => void;
};

const GameCenterCommonModal: React.FC<Props> = ({
  entityIdOverride,
  modalTypeOverride,
  onCloseOverride,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const permissions = usePermissionHelper();

  const tagPayload = useAppSelector((store) => store.tags.payload);
  const tagFetched = useAppSelector((store) => store.tags.isItemFetched);

  const collectionsPayload = useAppSelector(
    (store) => store.collections.payload,
  );
  const collectionFetched = useAppSelector(
    (store) => store.collections.isItemFetched,
  );

  const categoryPayload = useAppSelector((store) => store.categories.payload);
  const categoryFetched = useAppSelector(
    (store) => store.categories.isItemFetched,
  );

  const [tabs, setTabs] = useState<ModalGameCenterTabs[]>([
    ModalGameCenterTabs.main,
    ModalGameCenterTabs.seo,
    ModalGameCenterTabs.restriction,
    ModalGameCenterTabs.locales,
    ModalGameCenterTabs.trail,
  ]);

  const [activeTab, setActiveTab] = useState<ModalGameCenterTabs>(
    ModalGameCenterTabs.main,
  );

  const [hasMainData, setHasMainData] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [data, setData] = useState<any>();
  const [initialSeoData, setInitialSeoData] = useState<any>();
  const [dataRestriction, setDataRestriction] = useState<any>();
  const [isTabLoading, setIsTabLoading] = useState<boolean>(false);

  const modalType =
    modalTypeOverride ??
    (searchParams.get("gModalType") as GameCenterFormTypes | null);

  const formErrors = useFormErrors();
  const entityId = entityIdOverride ?? searchParams.get("entityId");
  const isReadOnly = !permissions?.update;

  const FORBIDDEN_TABS_BEFORE_CREATE = [
    ModalGameCenterTabs.seo,
    ModalGameCenterTabs.restriction,
    ModalGameCenterTabs.trail,
    ModalGameCenterTabs.games,
    ModalGameCenterTabs.locales,
  ];

  const locales = useAppSelector((store) => store.settings.locales);
  const [locale, setLocale] = useState<string>(locales[0]);
  const translation = useAppSelector((store) => {
    switch (modalType) {
      case GameCenterFormTypes.category:
        return (
          store.categories.payload?.translations?.find(
            (v) => v.locale === locale,
          ) ?? {}
        );
      case GameCenterFormTypes.collection:
        return (
          store.collections.payload?.translations?.find(
            (v) => v.locale === locale,
          ) ?? {}
        );
      case GameCenterFormTypes.tag:
        return (
          store.tags.payload?.translations?.find((v) => v.locale === locale) ??
          {}
        );

      default:
        return {};
    }
  });

  useEffect(() => {
    if (
      modalType === GameCenterFormTypes.tag ||
      modalType === GameCenterFormTypes.collection
    ) {
      setTabs([
        ModalGameCenterTabs.main,
        ModalGameCenterTabs.games,
        ModalGameCenterTabs.seo,
        ModalGameCenterTabs.restriction,
        ModalGameCenterTabs.locales,
        ModalGameCenterTabs.trail,
      ]);
    }
  }, [entityId, modalType]);

  useEffect(() => {
    if (entityId !== null) {
      setIsEdit(true);
      if (searchParams.get("tab") !== null) {
        setActiveTab(searchParams.get("tab") as ModalGameCenterTabs);
      }
      switch (modalType) {
        case GameCenterFormTypes.tag:
          dispatch(getTagItem(entityId ?? 1));
          setHasMainData(true);
          break;
        case GameCenterFormTypes.collection:
          dispatch(getCollectionItem(entityId ?? 1));
          setHasMainData(true);
          break;
        case GameCenterFormTypes.category:
          dispatch(getCategoryItem(entityId ?? 1));
          setHasMainData(true);
          break;

        default:
          break;
      }
    } else {
      setIsEdit(false);
    }
  }, [entityId, modalType]);

  // get locale by type of entity

  useEffect(() => {
    if (locales.length && !locale) {
      setLocale(locales[0]);
    }

    if (!entityId) return;

    if (activeTab !== ModalGameCenterTabs.locales) return;

    let isCurrentRequest = true;
    setIsTabLoading(true);

    let request;
    switch (modalType) {
      case GameCenterFormTypes.category:
        request = dispatch(getCategoryItemLocale({ id: entityId, locale }));
        break;
      case GameCenterFormTypes.collection:
        request = dispatch(getCollectionItemLocale({ id: entityId, locale }));
        break;
      case GameCenterFormTypes.tag:
        request = dispatch(getTagItemLocale({ id: entityId, locale }));
        break;
    }

    request?.finally(() => {
      if (isCurrentRequest) {
        setIsTabLoading(false);
      }
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [activeTab, dispatch, entityId, locale, locales, modalType]);

  const isLoaded = () => {
    if (
      entityId !== null &&
      !categoryFetched &&
      modalType === GameCenterFormTypes.category
    ) {
      return false;
    }
    if (
      entityId !== null &&
      !tagFetched &&
      modalType === GameCenterFormTypes.tag
    ) {
      return false;
    }
    if (
      entityId !== null &&
      !collectionFetched &&
      modalType === GameCenterFormTypes.collection
    ) {
      return false;
    }
    return true;
  };

  const loaded = isLoaded();

  const handleTabLoadingChange = useCallback((isLoading: boolean) => {
    setIsTabLoading(isLoading);
  }, []);

  const handleTabClick = (tab: ModalGameCenterTabs) => {
    const isCreateMode = !isEdit;

    if (
      isCreateMode &&
      !hasMainData &&
      FORBIDDEN_TABS_BEFORE_CREATE.includes(tab)
    ) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }

    setActiveTab(tab);
    setIsTabLoading(
      tab === ModalGameCenterTabs.seo ||
        tab === ModalGameCenterTabs.restriction ||
        tab === ModalGameCenterTabs.locales,
    );
  };

  const handleCloseClick = () => {
    if (onCloseOverride) onCloseOverride();
    else {
      navigate(`${location.pathname}`);
      dispatch(closeModal());
    }
    switch (modalType) {
      case GameCenterFormTypes.tag:
        dispatch(handleClearFields());
        break;
      case GameCenterFormTypes.collection:
        dispatch(handleClearFieldsCollections());
        break;
      case GameCenterFormTypes.category:
        dispatch(handleClearFieldsCategorries());
        break;

      default:
        break;
    }
  };

  const isCategoryFetching = useAppSelector((s) => s.categories.isItemFetching);
  const isCollectionFetching = useAppSelector(
    (s) => s.collections.isItemFetching,
  );
  const isTagFetching = useAppSelector((s) => s.tags.isItemFetching);
  const isTagItemLocaleFetching = useAppSelector(
    (s) => s.tags.isItemLocaleFetching,
  );
  const isCollectionItemLocaleFetching = useAppSelector(
    (s) => s.collections.isItemLocaleFetching,
  );
  const isCategoryItemLocaleFetching = useAppSelector(
    (s) => s.categories.isItemLocaleFetching,
  );

  const renderTab = () => {
    if (activeTab === ModalGameCenterTabs.main) {
      return (
        <CommonMainTab
          type={modalType ?? GameCenterFormTypes.tag}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={isReadOnly}
        />
      );
    }
    if (activeTab === ModalGameCenterTabs.seo) {
      return (
        <SeoTab
          currentData={data}
          onInitialData={setInitialSeoData}
          handleData={(e: any) => {
            setData(e);
          }}
          id={entityId ?? undefined}
          type={modalType ?? ""}
          isReadOnly={isReadOnly}
          onLoadingChange={handleTabLoadingChange}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
        />
      );
    }
    if (activeTab === ModalGameCenterTabs.games) {
      return (
        <TagsOrderListTab
          type={modalType ?? GameCenterFormTypes.tag}
          slug={
            modalType === GameCenterFormTypes.tag
              ? (tagPayload?.slug ?? "")
              : (collectionsPayload?.slug ?? "")
          }
          id={
            modalType === GameCenterFormTypes.tag
              ? (tagPayload?.id ?? 0)
              : (collectionsPayload?.id ?? 0)
          }
          isReadOnly={isReadOnly}
        />
      );
    }
    if (activeTab === ModalGameCenterTabs.restriction) {
      return (
        <RestrictionTab
          handleData={(e: any) => {
            setDataRestriction(e);
          }}
          type={modalType ?? ""}
          id={entityId ?? undefined}
          isReadOnly={isReadOnly}
          onLoadingChange={handleTabLoadingChange}
        />
      );
    }
    if (activeTab === ModalGameCenterTabs.locales) {
      const isLoading = () => {
        if (modalType === GameCenterFormTypes.category)
          return !isCategoryItemLocaleFetching;
        if (modalType === GameCenterFormTypes.collection)
          return !isCollectionItemLocaleFetching;
        if (modalType === GameCenterFormTypes.tag)
          return !isTagItemLocaleFetching;
      };

      return (
        <LocalTab
          translation={translation}
          isLoaded={isLoading()}
          onChange={(v) => handleChangeLocale(v, locale)}
          locales={locales}
          activeLocale={locale}
          setActiveLocale={setLocale}
          readOnly={
            isReadOnly ||
            isCategoryFetching ||
            isTagFetching ||
            isCollectionFetching
          }
        />
      );
    }
    if (activeTab === ModalGameCenterTabs.trail && modalType) {
      return <TrailTab type={modalType} />;
    }
  };

  const handleSetSeo = async () => {
    if (data) {
      try {
        const attributes = getChangedSeoAttributes(initialSeoData, data, [
          "headTitle",
          "metaTitle",
          "metaDescription",
          "ogTitle",
          "ogDescription",
          "twitterTitle",
          "twitterDescription",
          "description",
          "h1",
        ]);
        const fullDescription = getChangedSeoAttributes(
          initialSeoData,
          data,
          ["fullDescription"],
          { fullDescription: normalizeSeoHtml },
        );

        await instance.post(apiRoutes.setSeoAtrs, {
          instanceId: entityId,
          instanceType: modalType,
          locale: data.locale,
          attributes,
          ...fullDescription,
        });
        setInitialSeoData(data);
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        Object.entries(getSeoFieldErrors(error)).forEach(([field, code]) => {
          formErrors.setFieldError(field, code);
        });
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityUpdateError"),
            type: NotificationTypes.error,
          }),
        );
      }
    }
  };

  const handleSetRestricion = async () => {
    if (dataRestriction) {
      try {
        await instance.put(apiRoutes.setRestrictionsCountriesList, {
          instanceId: entityId,
          instanceType: modalType,
          geo: dataRestriction,
        });
        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityUpdateError"),
            type: NotificationTypes.error,
          }),
        );
      }
    }
  };

  const handleApply = async () => {
    if ((isEdit && !permissions?.update) || (!isEdit && !permissions?.create)) {
      return;
    }

    if (
      activeTab !== ModalGameCenterTabs.seo &&
      activeTab !== ModalGameCenterTabs.restriction &&
      activeTab !== ModalGameCenterTabs.locales
    ) {
      switch (modalType) {
        case GameCenterFormTypes.tag:
          if (!tagPayload) return;

          if (isNumericOnlySlug(tagPayload.slug)) {
            formErrors.resetErrors();
            formErrors.setFieldError("slug", "InvalidFormat");
            return;
          }

          if (!isEdit) {
            formErrors.resetErrors();
          }

          try {
            if (isEdit) {
              await dispatch(updateTag(tagPayload)).unwrap();
            } else {
              const newId = await dispatch(createTag(tagPayload)).unwrap();

              dispatch(
                handleUpdateFields({
                  ...tagPayload,
                  id: newId,
                }),
              );

              setHasMainData(true);

              navigate(
                `${location.pathname}?gModalType=${GameCenterFormTypes.tag}&entityId=${newId}`,
              );
            }

            dispatch(
              showNotification({
                title: t("success"),
                message: t(
                  isEdit ? "entityUpdateSuccess" : "entityCreateSuccess",
                ),
                type: NotificationTypes.success,
              }),
            );
          } catch (error) {
            handleApiError({
              error,
              t,
              dispatch,
              formErrors,
            });
          }
          break;
        case GameCenterFormTypes.collection:
          if (!collectionsPayload) return;

          if (isNumericOnlySlug(collectionsPayload.slug)) {
            formErrors.resetErrors();
            formErrors.setFieldError("slug", "InvalidFormat");
            return;
          }

          if (!isEdit) {
            formErrors.resetErrors();
          }

          try {
            if (isEdit) {
              await dispatch(updateCollection(collectionsPayload)).unwrap();
            } else {
              const newId = await dispatch(
                createCollection(collectionsPayload),
              ).unwrap();

              dispatch(
                handleUpdateFields({
                  ...collectionsPayload,
                  id: newId,
                }),
              );

              setHasMainData(true);

              navigate(
                `${location.pathname}?gModalType=${GameCenterFormTypes.collection}&entityId=${newId}`,
              );
            }

            dispatch(
              showNotification({
                title: t("success"),
                message: t(
                  isEdit ? "entityUpdateSuccess" : "entityCreateSuccess",
                ),
                type: NotificationTypes.success,
              }),
            );
          } catch (error) {
            handleApiError({
              error,
              t,
              dispatch,
              formErrors,
            });
          }
          break;
        case GameCenterFormTypes.category:
          if (!categoryPayload) return;

          if (isNumericOnlySlug(categoryPayload.slug)) {
            formErrors.resetErrors();
            formErrors.setFieldError("slug", "InvalidFormat");
            return;
          }

          if (!isEdit) {
            formErrors.resetErrors();
          }

          try {
            if (isEdit) {
              await dispatch(updateCategory(categoryPayload)).unwrap();
            } else {
              const newId = await dispatch(
                createCategory(categoryPayload),
              ).unwrap();

              dispatch(
                handleUpdateFields({
                  ...categoryPayload,
                  id: newId,
                }),
              );

              setHasMainData(true);
              navigate(
                `${location.pathname}?gModalType=${GameCenterFormTypes.category}&entityId=${newId}`,
              );
            }

            dispatch(
              showNotification({
                title: t("success"),
                message: t(
                  isEdit ? "entityUpdateSuccess" : "entityCreateSuccess",
                ),
                type: NotificationTypes.success,
              }),
            );
          } catch (error) {
            handleApiError({
              error,
              t,
              dispatch,
              formErrors,
            });
          }
          break;

        default:
          break;
      }
    } else {
      if (activeTab === ModalGameCenterTabs.seo) {
        handleSetSeo();
      } else if (activeTab === ModalGameCenterTabs.restriction) {
        handleSetRestricion();
      } else if (activeTab === ModalGameCenterTabs.locales) {
        updateLocale();
      }
    }
  };

  const handleChangeLocale = (e: TlocaleFieldUpdate, locale: string) => {
    switch (modalType) {
      // handleUpdateCollectionFields
      case GameCenterFormTypes.category:
        const updatingCategoryLocale = categoryPayload?.translations?.find(
          (l) => l.locale === locale,
        ) ?? { locale, title: "", description: "", isActive: false };

        const translationCategoryPrevious =
          categoryPayload?.translations?.filter((l) => l.locale !== locale) ||
          [];
        dispatch(
          handleUpdateFields({
            ...categoryPayload,
            translations: [
              ...translationCategoryPrevious,
              { ...updatingCategoryLocale, [e.field]: e.value },
            ],
          }),
        );
        return;
      case GameCenterFormTypes.collection:
        const updatingCollectionLocale = collectionsPayload?.translations?.find(
          (l) => l.locale === locale,
        ) ?? { locale, title: "", description: "", isActive: false };

        const translationCollectionPrevious =
          collectionsPayload?.translations?.filter(
            (l) => l.locale !== locale,
          ) || [];
        dispatch(
          handleUpdateCollectionFields({
            ...collectionsPayload,
            translations: [
              ...translationCollectionPrevious,
              { ...updatingCollectionLocale, [e.field]: e.value },
            ],
          }),
        );
        return;
      case GameCenterFormTypes.tag:
        const updatingTagLocale = tagPayload?.translations?.find(
          (l) => l.locale === locale,
        ) ?? { locale, title: "", description: "", isActive: false };

        const translationTagPrevious =
          tagPayload?.translations?.filter((l) => l.locale !== locale) || [];
        dispatch(
          handleUpdateTagFields({
            ...tagPayload,
            translations: [
              ...translationTagPrevious,
              { ...updatingTagLocale, [e.field]: e.value },
            ],
          }),
        );
        return;
    }
  };

  const updateLocale = () => {
    if (!entityId) return;

    switch (modalType) {
      case GameCenterFormTypes.category:
        dispatch(
          updateCategoryItemLocale({ id: entityId, locale: translation }),
        ).unwrap();

        return;
      case GameCenterFormTypes.collection:
        dispatch(
          updateCollectionItemLocale({ id: entityId, locale: translation }),
        ).unwrap();

        return;
      case GameCenterFormTypes.tag:
        dispatch(
          updateTagItemLocale({ id: entityId, locale: translation }),
        ).unwrap();

        return;
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t(modalType ?? "")}</Modal.Header>

      {!loaded ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            canBeClicked={hasMainData}
            onClick={handleTabClick}
          />

          <Modal.Content className={styles.gameCenterContentWrapper}>
            {renderTab()}
          </Modal.Content>

          {(isEdit ? permissions?.update : permissions?.create) &&
            activeTab !== ModalGameCenterTabs.trail && (
              <Modal.Footer
                className={
                  activeTab === ModalGameCenterTabs.games
                    ? styles.gamesEmptyFooter
                    : undefined
                }
              >
                {activeTab !== ModalGameCenterTabs.games &&
                  (isTabLoading ? (
                    <ModalFooterSkeleton />
                  ) : (
                    <>
                      <Button
                        isSecondary
                        txt={t("cancel")}
                        handleClick={handleCloseClick}
                      />
                      <Button
                        isPrimary
                        handleClick={handleApply}
                        txt={t("save")}
                        isLoad={
                          modalType === GameCenterFormTypes.collection
                            ? isCollectionFetching
                            : modalType === GameCenterFormTypes.tag
                              ? isTagFetching
                              : isCategoryFetching
                        }
                        disabled={
                          modalType === GameCenterFormTypes.collection
                            ? isCollectionFetching
                            : modalType === GameCenterFormTypes.tag
                              ? isTagFetching
                              : isCategoryFetching
                        }
                      />
                    </>
                  ))}
              </Modal.Footer>
            )}
        </>
      )}
    </Modal>
  );
};

export default GameCenterCommonModal;
